import { enhancedNarrativePlainText } from './reports/enhancedNarrativeHtml'
import { importWithChunkRecovery } from './chunkLoadRecovery'

const fileName = (propertyName: string) => `${propertyName.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '') || 'property'}-enhanced-report`
type PdfSlice = { sourceY: number; height: number }

// Prefer a real rendered line/block boundary near the bottom of each page. This keeps the PDF
// viewer's page separator from cutting through glyphs while still using most of the page.
export const buildEnhancedReportPdfSlices = (
    totalHeight: number,
    pageCapacity: number,
    safeBreaks: number[],
    findRenderedWhitespace?: (minimum: number, maximum: number) => number | null,
): PdfSlice[] => {
    if (totalHeight <= 0 || pageCapacity <= 0) return []
    const breaks = Array.from(new Set(safeBreaks
        .filter((value) => Number.isFinite(value) && value > 0 && value < totalHeight)
        .map((value) => Math.round(value))))
        .sort((left, right) => left - right)
    const slices: PdfSlice[] = []
    let sourceY = 0
    while (sourceY < totalHeight) {
        const target = Math.min(totalHeight, sourceY + pageCapacity)
        let end = target
        if (target < totalHeight) {
            const minimumUsefulPage = sourceY + pageCapacity * 0.55
            const renderedBreak = findRenderedWhitespace?.(Math.ceil(minimumUsefulPage), Math.floor(target - 2))
            if (renderedBreak && renderedBreak > sourceY && renderedBreak < target) {
                end = renderedBreak
            } else {
                const candidates = breaks.filter((value) => value >= minimumUsefulPage && value <= target - 2)
                if (candidates.length) end = candidates[candidates.length - 1]
            }
        }
        if (end <= sourceY) end = target
        slices.push({ sourceY, height: end - sourceY })
        sourceY = end
    }
    return slices
}

export const findRenderedWhitespaceRow = (
    pixels: Uint8ClampedArray,
    width: number,
    height: number,
    sampleEvery: number,
    maximumInkPixels: number,
    minimumClearRows: number,
) => {
    const rowStride = width * 4
    let clearStart = -1
    let lastClearBreak: number | null = null
    for (let row = 0; row < height; row += 1) {
        let inkPixels = 0
        for (let x = 0; x < width; x += sampleEvery) {
            const offset = row * rowStride + x * 4
            if (pixels[offset + 3] < 32) continue
            const red = pixels[offset]
            const green = pixels[offset + 1]
            const blue = pixels[offset + 2]
            const luminance = red * 0.2126 + green * 0.7152 + blue * 0.0722
            const saturation = Math.max(red, green, blue) - Math.min(red, green, blue)
            if (luminance < 225 || saturation > 45) inkPixels += 1
            if (inkPixels > maximumInkPixels) break
        }
        const clear = inkPixels <= maximumInkPixels
        if (clear && clearStart < 0) clearStart = row
        if ((!clear || row === height - 1) && clearStart >= 0) {
            const clearEnd = clear && row === height - 1 ? row + 1 : row
            if (clearEnd - clearStart >= minimumClearRows) {
                lastClearBreak = Math.round((clearStart + clearEnd) / 2)
            }
            clearStart = -1
        }
    }
    return lastClearBreak
}

const createCanvasWhitespaceFinder = (canvas: HTMLCanvasElement) => {
    const context = canvas.getContext('2d')
    if (!context) return undefined
    const left = Math.floor(canvas.width * 0.035)
    const width = Math.max(1, canvas.width - left * 2)
    const sampleEvery = Math.max(1, Math.floor(width / 700))
    const minimumClearRows = Math.max(4, Math.round(canvas.width / 794 * 3))

    return (minimum: number, maximum: number) => {
        const top = Math.max(0, Math.floor(minimum))
        const bottom = Math.min(canvas.height - 1, Math.ceil(maximum))
        if (bottom <= top) return null
        try {
            const height = bottom - top + 1
            const pixels = context.getImageData(left, top, width, height).data
            const sampledPixels = Math.ceil(width / sampleEvery)
            const maximumInkPixels = Math.max(2, Math.floor(sampledPixels * 0.002))
            const relativeBreak = findRenderedWhitespaceRow(
                pixels,
                width,
                height,
                sampleEvery,
                maximumInkPixels,
                minimumClearRows,
            )
            return relativeBreak === null ? null : top + relativeBreak
        } catch {
            // Cross-origin canvas restrictions can prevent pixel inspection. DOM line boundaries
            // remain available as the fallback supplied to buildEnhancedReportPdfSlices.
            return null
        }
    }
}

const downloadBlob = (blob: Blob, name: string) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = name
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
}

export const copyEnhancedNarrativeHtml = async (reportHtml: string, previewFrame?: HTMLIFrameElement | null) => {
    const plainText = enhancedNarrativePlainText(reportHtml)
    if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
        try {
            await navigator.clipboard.write([new ClipboardItem({
                'text/html': new Blob([reportHtml], { type: 'text/html' }),
                'text/plain': new Blob([plainText], { type: 'text/plain' }),
            })])
            return
        } catch {
            // Permission and MIME support vary by browser; the selected-document fallback below
            // still places rich HTML on the clipboard when the legacy copy command is available.
        }
    }

    const previewDocument = previewFrame?.contentDocument
    if (previewDocument?.body && typeof previewDocument.execCommand === 'function') {
        const selection = previewDocument.getSelection()
        const range = previewDocument.createRange()
        range.selectNodeContents(previewDocument.body)
        selection?.removeAllRanges()
        selection?.addRange(range)
        const copied = previewDocument.execCommand('copy')
        selection?.removeAllRanges()
        if (copied) return
    }
    throw new Error('Formatted copy is not available in this browser. Select the report text and copy it manually.')
}

export const exportEnhancedNarrativeWord = async (reportHtml: string, propertyName: string) => {
    const { asBlob } = await importWithChunkRecovery(() => import('html-docx-js-typescript'))
    const output = await asBlob(reportHtml, {
        margins: { top: 720, right: 720, bottom: 720, left: 720, header: 360, footer: 360, gutter: 0 },
    })
    const blob = output instanceof Blob
        ? output
        : new Blob([new Uint8Array(output)], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
    downloadBlob(blob, `${fileName(propertyName)}.docx`)
}

const waitForFrame = (reportHtml: string) => new Promise<HTMLIFrameElement>((resolve, reject) => {
    const frame = document.createElement('iframe')
    frame.setAttribute('sandbox', 'allow-same-origin')
    frame.setAttribute('aria-hidden', 'true')
    Object.assign(frame.style, {
        position: 'fixed',
        left: '-100000px',
        top: '0',
        width: '794px',
        height: '1123px',
        border: '0',
        opacity: '0',
        pointerEvents: 'none',
    })
    const timeout = window.setTimeout(() => {
        frame.remove()
        reject(new Error('The rich report took too long to prepare. Please try again.'))
    }, 15000)
    frame.onload = () => {
        window.clearTimeout(timeout)
        resolve(frame)
    }
    frame.onerror = () => {
        window.clearTimeout(timeout)
        frame.remove()
        reject(new Error('The rich report could not be prepared. Please try again.'))
    }
    frame.srcdoc = reportHtml
    document.body.appendChild(frame)
})

const waitForAssets = async (frame: HTMLIFrameElement) => {
    const reportDocument = frame.contentDocument
    if (!reportDocument) throw new Error('The rich report could not be read for PDF export.')
    const images = Array.from(reportDocument.images).filter((image) => !image.complete)
    await Promise.all(images.map((image) => new Promise<void>((resolve) => {
        const done = () => resolve()
        image.addEventListener('load', done, { once: true })
        image.addEventListener('error', done, { once: true })
        window.setTimeout(done, 5000)
    })))
    await reportDocument.fonts?.ready
    const height = Math.max(reportDocument.documentElement.scrollHeight, reportDocument.body.scrollHeight, 1123)
    frame.style.height = `${height}px`
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
    return reportDocument
}

const collectSafeBreaks = (reportDocument: Document, pageCapacity: number) => {
    const rootTop = reportDocument.documentElement.getBoundingClientRect().top
    const breaks: number[] = []
    const atomicSelector = 'tr, figure, pre, blockquote, img, svg, canvas, video'
    const headings = 'h1, h2, h3, h4, h5, h6'
    const relativeBottom = (rect: DOMRect) => rect.bottom - rootTop
    const tree = reportDocument.createTreeWalker(reportDocument.body, NodeFilter.SHOW_TEXT)
    let node = tree.nextNode()
    while (node) {
        const parent = node.parentElement
        if (node.textContent?.trim() && parent && !parent.closest(headings)) {
            const atomic = parent.closest(atomicSelector)
            const atomicRect = atomic?.getBoundingClientRect()
            // Small atomic blocks get one break after the whole block. Oversized blocks retain
            // line-level breaks so they can still span pages without being cropped.
            if (!atomicRect || atomicRect.height > pageCapacity * 0.85) {
                const range = reportDocument.createRange()
                range.selectNodeContents(node)
                Array.from(range.getClientRects()).forEach((rect) => {
                    if (rect.width > 0 && rect.height > 0) breaks.push(relativeBottom(rect))
                })
                range.detach()
            }
        }
        node = tree.nextNode()
    }
    reportDocument.querySelectorAll(atomicSelector).forEach((element) => {
        const rect = element.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0 && rect.height <= pageCapacity * 0.85) breaks.push(relativeBottom(rect))
    })
    // A heading may start a new page, but it should not be left alone at the bottom of one.
    reportDocument.querySelectorAll(headings).forEach((element) => {
        const rect = element.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) breaks.push(rect.top - rootTop)
    })
    return breaks
}

export const exportEnhancedNarrativeHtmlPdf = async (reportHtml: string, propertyName: string) => {
    const frame = await waitForFrame(reportHtml)
    try {
        const reportDocument = await waitForAssets(frame)
        const [{ default: html2canvas }, { jsPDF: JsPdf }] = await Promise.all([
            importWithChunkRecovery(() => import('html2canvas')),
            importWithChunkRecovery(() => import('jspdf')),
        ])
        const reportWidth = Math.max(reportDocument.documentElement.scrollWidth, reportDocument.body.scrollWidth, 794)
        const canvas = await html2canvas(reportDocument.documentElement, {
            backgroundColor: '#ffffff',
            logging: false,
            scale: Math.max(1, Math.min(2, 2200 / reportWidth)),
            useCORS: true,
            windowWidth: reportWidth,
            windowHeight: reportDocument.documentElement.scrollHeight,
        })
        const pdf = new JsPdf({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true })
        pdf.setProperties({ title: `${propertyName} — Enhanced report`, author: 'HotelWorld AI' })
        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()
        const pageMargin = 36
        const sourcePageHeight = Math.floor(canvas.width * (pageHeight - pageMargin * 2) / pageWidth)
        const cssHeight = Math.max(reportDocument.documentElement.scrollHeight, reportDocument.body.scrollHeight, 1)
        const verticalScale = canvas.height / cssHeight
        const safeBreaks = collectSafeBreaks(reportDocument, sourcePageHeight / verticalScale)
            .map((value) => value * verticalScale)
        const slices = buildEnhancedReportPdfSlices(
            canvas.height,
            sourcePageHeight,
            safeBreaks,
            createCanvasWhitespaceFinder(canvas),
        )
        slices.forEach((slice, page) => {
            if (page) pdf.addPage('a4', 'portrait')
            const pageCanvas = document.createElement('canvas')
            pageCanvas.width = canvas.width
            pageCanvas.height = slice.height
            const context = pageCanvas.getContext('2d')
            if (!context) throw new Error('Your browser could not prepare the PDF. Please try the Word download.')
            context.fillStyle = '#ffffff'
            context.fillRect(0, 0, pageCanvas.width, pageCanvas.height)
            context.drawImage(canvas, 0, slice.sourceY, canvas.width, slice.height, 0, 0, canvas.width, slice.height)
            const renderedHeight = slice.height / canvas.width * pageWidth
            pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.96), 'JPEG', 0, pageMargin, pageWidth, renderedHeight, undefined, 'FAST')
            pageCanvas.width = 0
            pageCanvas.height = 0
        })
        pdf.save(`${fileName(propertyName)}.pdf`)
    } finally {
        frame.remove()
    }
}
